import type {
  ChatModelAdapter,
  ChatModelRunResult,
  ThreadAssistantMessagePart,
  ThreadMessage,
} from "@assistant-ui/react";
import type { KyInstance } from "ky";
import {
  AGENT_ACTIVITY_KEY,
  applyActivityEvent,
  NO_ACTIVITY,
  type AgentActivity,
} from "./agentActivity";
import { parseEventStream } from "./eventStream";
import { genuiToolCallPart } from "./genui/part";
import { MESSAGE_BACKEND_ID_KEY } from "./messageIdentity";

/** An interactive (`type: "input"`) GenUI component diverted out of the message list. */
export interface ActiveInputComponent {
  /** Stable component instance id (the tool_call_id). */
  componentId: string;
  /** Component name the input registry dispatches on (e.g. `render_choices`). */
  component: string;
  /** Component props. */
  props: Record<string, unknown>;
}

/**
 * The host's currently open conversation, as cached from the bridge's `conversation` context
 * channel. Structurally matches the bridge's `ContextEntry` so `channel("conversation").getEntry`
 * can be passed straight through without this package importing `@repo/bridge`.
 */
export interface HostConversationEntry {
  /** The roster id of the open thread. */
  id: string;
  /** Type-specific extras; the transport reads `entityType` ("account" | "group" | "distlist"). */
  metadata: Record<string, string>;
}

/** Entity types the agent's conversation_context contract accepts (parse_conversation_context). */
const CONVERSATION_ENTITY_TYPES = new Set(["account", "group", "distlist"]);

/** The agent rejects identifiers longer than this (parse_conversation_context). */
const CONVERSATION_ID_MAX_LENGTH = 256;

function isValidConversationId(value: string | undefined): value is string {
  return (
    typeof value === "string" && value.length > 0 && value.length <= CONVERSATION_ID_MAX_LENGTH
  );
}

/**
 * The `conversation_context` body field for the host's open thread, or undefined when the entry
 * can't satisfy the agent's validated contract (the agent 400s on a malformed value, so anything
 * incomplete — e.g. a host that doesn't push `entityType` yet — must be omitted, not guessed).
 * Deliberately no org field: the agent takes the organization from the JWT, never the client.
 */
function toConversationContext(
  entry: HostConversationEntry | undefined,
): Record<string, string> | undefined {
  if (!entry || !isValidConversationId(entry.id)) return undefined;
  const entityType = entry.metadata?.entityType;
  if (!entityType || !CONVERSATION_ENTITY_TYPES.has(entityType)) return undefined;
  return { roster_id: entry.id, entity_type: entityType };
}

export interface AgentTransportConfig {
  /** ky instance prefixed at the agent base URL (see `createAgentApi`). */
  api: KyInstance;
  /** Stable identifier for this agent chat session, sent with every turn. */
  sessionId: string;
  /**
   * Lazy read of the host's current conversation — evaluated per request so a change that
   * lands mid-turn is picked up on the next one. Omitted from the body when undefined.
   * `id` is always sent as `conversation_id`; when the metadata carries a valid `entityType`,
   * the validated `conversation_context` field is sent too, which is what lets the agent bind
   * "summarize this conversation" to the open thread.
   */
  getConversation?: () => HostConversationEntry | undefined;
  /**
   * Lazy read of the host's current patient context MRN — evaluated per request alongside
   * `getConversation`. Omitted from the body when undefined.
   */
  getMrn?: () => string | undefined;
  /**
   * Called when an `input` GenUI component arrives. Input components are not
   * rendered inline in the message list — they surface above the composer via a
   * side channel (see `AgentProvider`). Display components are unaffected.
   */
  onInputComponent?: (component: ActiveInputComponent) => void;
  onActivity?: (activities: readonly AgentActivity[]) => void;
  /** "ios" | "android" | "web" — omitted from the body when unknown (e.g. standalone/dev). */
  clientApp?: string;
  /** The ios/android/web app version. Omitted from the body when unknown. */
  clientAppVersion?: string;
  /** Present only when running inside the Desktop App (Electron or the legacy .NET wrapper). */
  clientAppDesktopVersion?: string;
}

/** Wire-format message the agent endpoint expects. */
interface WireMessage {
  role: string;
  content: string;
}

/** Wire-format message dict carried by the terminal `done` event. */
interface DoneMessage {
  type?: string;
  id?: string;
  content?: string | { type?: string; text?: string }[];
}

/** Union of every `data` shape the stream emits; each event reads only its own fields. */
interface StreamEventData {
  /** `token` (delta) and `message` (authoritative full text). */
  text?: string;
  id?: string;
  /** `"ai" | "human"` on message_start/_end; `"display" | "input"` on ui_component. */
  type?: string;
  /** `done` — may carry the full session history, not just this turn. */
  messages?: DoneMessage[];
  /** `ui_component`. */
  component_id?: string;
  component?: string;
  props?: Record<string, unknown>;
  schema_version?: string;
  sub_agent?: string;
  invocation_id?: string;
  name?: string;
  tool_call_id?: string;
}

/** Assistant-ui message part shapes this transport emits: streamed text plus genui tool-calls. */
type EmittedPart = Extract<ThreadAssistantMessagePart, { type: "text" | "tool-call" }>;

/** Flatten an assistant-ui message's parts into a single text string. */
function toWireMessage(message: ThreadMessage): WireMessage {
  const content = message.content
    .filter((part): part is Extract<typeof part, { type: "text" }> => part.type === "text")
    .map((part) => part.text)
    .join("");
  return { role: message.role, content };
}

/** Flatten a done-payload message's content (string or parts array) into text. */
function doneMessageText(message: DoneMessage): string | undefined {
  const { content } = message;
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    return content
      .filter((part): part is { type: string; text: string } => part.type === "text")
      .map((part) => part.text)
      .join("");
  }
  return undefined;
}

/**
 * Id of the assistant message this turn produced, if any. The done payload may carry the full
 * session history, so prefer the AI message whose content matches the text we actually streamed
 * — a trailing tool-call step or an older identical turn can't shadow the current message. Falls
 * back to the last AI message when no content match exists.
 */
function finalAssistantMessageId(
  messages: DoneMessage[] | undefined,
  streamedText: string,
): string | undefined {
  if (!messages) return undefined;
  let lastAiId: string | undefined;
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message?.type !== "ai" || !message.id) continue;
    lastAiId ??= message.id;
    if (doneMessageText(message) === streamedText) return message.id;
  }
  return lastAiId;
}

function toRunResult(
  content: EmittedPart[],
  backendId: string | undefined,
  activities: readonly AgentActivity[],
): ChatModelRunResult {
  const custom: Record<string, unknown> = {};
  if (backendId) custom[MESSAGE_BACKEND_ID_KEY] = backendId;
  if (activities.length > 0) custom[AGENT_ACTIVITY_KEY] = activities;
  return {
    content: [...content],
    ...(Object.keys(custom).length > 0 ? { metadata: { custom } } : {}),
  };
}

/**
 * Adapter that streams a turn against the Tiger Agent endpoint. Each yield carries the full
 * accumulated content parts so far (text plus any GenUI components) — assistant-ui replaces
 * content on every yield, so we send the cumulative array rather than deltas.
 */
export function createAgentTransport(config: AgentTransportConfig): ChatModelAdapter {
  return {
    async *run({ messages, abortSignal }) {
      const conversation = config.getConversation?.();
      const conversationContext = toConversationContext(conversation);
      const mrn = config.getMrn?.();

      let activities: readonly AgentActivity[] = NO_ACTIVITY;
      config.onActivity?.(activities);

      // ky throws HTTPError on non-2xx, so no manual response.ok check. The stream
      // opts out of ky's per-attempt timeout and retry: an agent turn streams for an
      // unbounded time and is non-idempotent (the server checkpoints every turn).
      const response = await config.api.post("invocations", {
        signal: abortSignal,
        timeout: false,
        retry: 0,
        json: {
          messages: messages.map(toWireMessage),
          stream: true,
          session_id: config.sessionId,
          ...(conversation?.id ? { conversation_id: conversation.id } : {}),
          ...(conversationContext ? { conversation_context: conversationContext } : {}),
          ...(mrn ? { mrn } : {}),
          ...(config.clientApp ? { client_app: config.clientApp } : {}),
          ...(config.clientAppVersion ? { client_app_version: config.clientAppVersion } : {}),
          ...(config.clientAppDesktopVersion
            ? { client_app_desktop_version: config.clientAppDesktopVersion }
            : {}),
        },
      });

      // Ordered parts for the turn. assistant-ui replaces content on every yield, so we
      // re-yield the whole accumulated array (text + genui components) rather than deltas.
      const content: EmittedPart[] = [];
      // The text part currently being streamed. Held by reference across a genui part so the
      // authoritative `message` event updates the text above the component, not below it.
      let activeText: { type: "text"; text: string } | undefined;
      // Backend id of the message this turn is producing, known as soon as the server
      // announces the message start — before any token or the terminal done event.
      let backendId: string | undefined;

      for await (const { event, data } of parseEventStream<StreamEventData>(
        response,
        "Agent request",
      )) {
        const nextActivities = applyActivityEvent(activities, event, data);
        if (nextActivities !== activities) {
          activities = nextActivities;
          config.onActivity?.(activities);
          yield toRunResult(content, backendId, activities);
        }

        switch (event) {
          case "token": {
            if (!activeText) {
              activeText = { type: "text", text: "" };
              content.push(activeText);
            }
            activeText.text += data?.text ?? "";
            yield toRunResult(content, backendId, activities);
            break;
          }
          case "message": {
            // Authoritative full text for the current text part.
            const full = data?.text;
            if (full === undefined) break;
            if (!activeText) {
              activeText = { type: "text", text: full };
              content.push(activeText);
            } else {
              activeText.text = full;
            }
            yield toRunResult(content, backendId, activities);
            break;
          }
          case "message_start": {
            // Announce the message id as early as possible (the last ai start wins): the
            // runtime merges this metadata into the message on every yield, so even a turn
            // that fails mid-stream leaves the message rateable.
            if (data?.type === "ai" && data.id) backendId = data.id;
            yield toRunResult(content, backendId, activities);
            break;
          }
          case "ui_component": {
            if (!data?.component_id || !data.component) break;
            if (data.type === "input") {
              // Interactive component — diverted to the composer-adjacent input
              // area (see AgentProvider), never added to the message list.
              config.onInputComponent?.({
                componentId: data.component_id,
                component: data.component,
                props: data.props ?? {},
              });
              break;
            }
            // Display component — rendered inline as a genui tool-call part.
            content.push(
              genuiToolCallPart({
                componentId: data.component_id,
                component: data.component,
                props: data.props,
                schemaVersion: data.schema_version,
              }),
            );
            yield toRunResult(content, backendId, activities);
            break;
          }
          case "done": {
            // Fallback when message_start never carried an id; the stamped id then comes
            // from the just-finished response in the terminal payload.
            const messageId =
              backendId ?? finalAssistantMessageId(data?.messages, activeText?.text ?? "");
            if (messageId) yield toRunResult(content, messageId, activities);
            break;
          }
          // message_end carries no incremental content.
          default:
            break;
        }
      }
    },
  };
}
